import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BadgeIndianRupee, IdCard, PlusCircle, Download, FileDown } from "lucide-react";
import { createPdfDoc, withDefaults, finalizePdf } from "@/lib/pdf";
import { monthLocalISO } from "@/lib/dates";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const monthNow = () => monthLocalISO(); // YYYY-MM in local (IST) tz — never toISOString()

const maskBank = (v) => {
  if (!v) return "";
  const s = String(v).replace(/\s+/g, "");
  if (s.length <= 4) return "X".repeat(s.length);
  return "X".repeat(Math.max(6, s.length - 4)) + s.slice(-4);
};

export default function AdminSalary() {
  const [tab, setTab] = useState("masters");
  const [promoters, setPromoters] = useState([]);
  const [masters, setMasters] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(monthNow());
  const [editForm, setEditForm] = useState(null);   // salary master edit
  const [bookForm, setBookForm] = useState(null);   // book salary form
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, m, b] = await Promise.all([
        api.get("/users?role=Promoter"),
        api.get("/admin/salary/masters"),
        api.get(`/admin/salary/bookings?month=${month}`),
      ]);
      setPromoters(p.data || []);
      setMasters(m.data || []);
      setBookings(b.data || []);
    } catch { toast.error("Failed to load salary data"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const masterByUser = useMemo(() => {
    const map = {};
    masters.forEach(m => { map[m.user_id] = m; });
    return map;
  }, [masters]);

  const bookingByUser = useMemo(() => {
    const map = {};
    bookings.forEach(b => { map[b.user_id] = b; });
    return map;
  }, [bookings]);

  // Summary strip (visible on the screen + written into every export)
  const summary = useMemo(() => {
    if (tab === "masters") {
      const total = promoters.length;
      const configured = promoters.filter(p => masterByUser[p.id]).length;
      const monthlyOutlay = promoters.reduce((a, p) => a + ((masterByUser[p.id]?.monthly_salary) || p.monthly_salary || 0), 0);
      return { total, configured, pending: total - configured, monthlyOutlay };
    }
    const total = promoters.length;
    const booked = promoters.filter(p => bookingByUser[p.id]).length;
    const amountBooked = bookings.reduce((a, b) => a + (b.net_amount || 0), 0);
    return { total, booked, pending: total - booked, amountBooked };
  }, [tab, promoters, masterByUser, bookingByUser, bookings]);

  const exportPDF = async () => {
    const doc = await createPdfDoc();
    doc.setFontSize(14); doc.setTextColor(225, 29, 42);
    doc.text(tab === "masters" ? "Promoter Salary — Master Register" : `Promoter Salary — Bookings (${month})`, 12, 12);
    doc.setFontSize(9); doc.setTextColor(60);
    doc.text("Powered by BBIPL for CCI", 12, 17);
    // Summary strip
    if (tab === "masters") {
      autoTable(doc, withDefaults({
        startY: 22,
        head: [["Total promoters", "Configured", "Pending", "Total monthly outlay (₹)"]],
        body: [[summary.total, summary.configured, summary.pending, summary.monthlyOutlay.toLocaleString("en-IN")]],
      }));
      autoTable(doc, withDefaults({
        startY: doc.lastAutoTable.finalY + 6,
        head: [["Promoter", "Phone", "Monthly ₹", "Aadhaar", "PAN", "Bank"]],
        body: promoters.map(p => {
          const m = masterByUser[p.id] || {};
          return [
            p.name, p.phone,
            (m.monthly_salary || p.monthly_salary || 0).toLocaleString("en-IN"),
            m.aadhaar_masked || "—",
            m.pan_masked || "—",
            m.bank_account ? `${maskBank(m.bank_account)} · ${m.ifsc || "—"}` : "—",
          ];
        }),
      }));
    } else {
      autoTable(doc, withDefaults({
        startY: 22,
        head: [["Total promoters", "Booked", "Pending", `Amount booked (${month}) ₹`]],
        body: [[summary.total, summary.booked, summary.pending, summary.amountBooked.toLocaleString("en-IN")]],
      }));
      autoTable(doc, withDefaults({
        startY: doc.lastAutoTable.finalY + 6,
        head: [["Promoter", "Phone", "Amount ₹", "Booked by", "Booked at"]],
        body: promoters.map(p => {
          const b = bookingByUser[p.id];
          return [p.name, p.phone,
            b ? b.net_amount.toLocaleString("en-IN") : "—",
            b ? b.booked_by_name : "—",
            b ? (b.booked_at || "").slice(0, 16).replace("T", " ") : "—",
          ];
        }),
      }));
    }
    finalizePdf(doc, { footer: `Generated by Coke@9 OCT - powered By BBIPL · ${new Date().toLocaleString("en-IN")}` });
    doc.save(`Coke9_Salary_${tab}_${tab === "bookings" ? month : monthNow()}.pdf`);
    toast.success("PDF downloaded");
  };

  const exportXLSX = () => {
    const wb = XLSX.utils.book_new();
    const header = ["Coke@9 OCT · Promoter Salary — " + (tab === "masters" ? "Master Register" : `Bookings (${month})`)];
    const hdr2 = ["Powered by BBIPL for CCI"];
    let rows;
    if (tab === "masters") {
      rows = [
        header, hdr2, [],
        ["Total promoters", "Configured", "Pending", "Total monthly outlay (₹)"],
        [summary.total, summary.configured, summary.pending, summary.monthlyOutlay],
        [],
        ["Promoter", "Phone", "Monthly ₹", "Aadhaar (masked)", "PAN (masked)", "Bank (masked) · IFSC"],
        ...promoters.map(p => {
          const m = masterByUser[p.id] || {};
          return [
            p.name, p.phone,
            m.monthly_salary || p.monthly_salary || 0,
            m.aadhaar_masked || "—",
            m.pan_masked || "—",
            m.bank_account ? `${maskBank(m.bank_account)} · ${m.ifsc || "—"}` : "—",
          ];
        }),
      ];
    } else {
      rows = [
        header, hdr2, [],
        ["Total promoters", "Booked", "Pending", `Amount booked (${month}) ₹`],
        [summary.total, summary.booked, summary.pending, summary.amountBooked],
        [],
        ["Promoter", "Phone", "Amount ₹", "Booked by", "Booked at"],
        ...promoters.map(p => {
          const b = bookingByUser[p.id];
          return [p.name, p.phone,
            b ? b.net_amount : "—",
            b ? b.booked_by_name : "—",
            b ? (b.booked_at || "").slice(0, 16).replace("T", " ") : "—",
          ];
        }),
      ];
    }
    rows.push([], ["", "", "Generated by Coke@9 OCT - powered By BBIPL"]);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, tab === "masters" ? "Salary Master" : "Salary Bookings");
    XLSX.writeFile(wb, `Coke9_Salary_${tab}_${tab === "bookings" ? month : monthNow()}.xlsx`);
    toast.success("XLSX downloaded");
  };

  const openMasterEdit = (u) => {
    const m = masterByUser[u.id] || {};
    setEditForm({
      user_id: u.id,
      user_name: u.name,
      monthly_salary: m.monthly_salary || u.monthly_salary || 0,
      aadhaar: "",   // NEVER pre-fill — always masked; admin re-enters if updating
      pan: "",
      bank_account: m.bank_account || "",
      ifsc: m.ifsc || "",
      notes: m.notes || "",
      aadhaar_masked: m.aadhaar_masked,
      pan_masked: m.pan_masked,
    });
  };

  const saveMaster = async () => {
    if (!editForm.monthly_salary && editForm.monthly_salary !== 0) return toast.error("Enter monthly salary");
    setSaving(true);
    try {
      const payload = { ...editForm };
      // Only send aadhaar/pan when admin actually typed new values
      if (!payload.aadhaar) delete payload.aadhaar;
      if (!payload.pan) delete payload.pan;
      await api.post("/admin/salary/masters", payload);
      toast.success("Salary master saved");
      setEditForm(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const openBook = (u) => {
    const m = masterByUser[u.id];
    setBookForm({
      user_id: u.id,
      user_name: u.name,
      month,
      net_amount: m?.monthly_salary || u.monthly_salary || 0,
      remarks: "",
    });
  };

  const doBook = async () => {
    if (!bookForm.net_amount) return toast.error("Enter net amount");
    setSaving(true);
    try {
      await api.post("/admin/salary/bookings", bookForm);
      toast.success(`Salary booked for ${bookForm.user_name} — ${bookForm.month} (auto-expense created)`);
      setBookForm(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Booking failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader />
      <div className="flex flex-1">
        <SideNav />
        <main className="flex-1 p-4 md:p-8 max-w-6xl mx-auto w-full">
          <div className="flex items-center gap-3 mb-6">
            <BadgeIndianRupee className="h-6 w-6 text-brand-red" />
            <h1 className="font-display text-2xl md:text-3xl font-bold" data-testid="salary-title">Promoter Salary</h1>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList data-testid="salary-tabs">
                <TabsTrigger value="masters" data-testid="tab-masters">Salary Master</TabsTrigger>
                <TabsTrigger value="bookings" data-testid="tab-bookings">Monthly Booking</TabsTrigger>
              </TabsList>
            </Tabs>
            {tab === "bookings" && (
              <Input data-testid="salary-month-input" type="month" className="w-40 h-9"
                value={month} onChange={(e) => setMonth(e.target.value)} />
            )}
            <div className="flex-1" />
            <Button variant="outline" size="sm" data-testid="salary-pdf-btn" onClick={exportPDF}>
              <Download className="h-4 w-4 mr-2" /> PDF
            </Button>
            <Button size="sm" className="bg-brand-red hover:bg-[#B31721]" data-testid="salary-xlsx-btn" onClick={exportXLSX}>
              <FileDown className="h-4 w-4 mr-2" /> Excel (XLSX)
            </Button>
          </div>

          {/* Summary strip */}
          <Card className="p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="salary-summary-strip">
            {tab === "masters" ? (
              <>
                <div><div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Total promoters</div><div className="text-2xl font-bold" data-testid="summary-total">{summary.total}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Configured</div><div className="text-2xl font-bold text-green-700" data-testid="summary-configured">{summary.configured}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Pending</div><div className="text-2xl font-bold text-amber-700" data-testid="summary-pending">{summary.pending}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Monthly outlay</div><div className="text-2xl font-bold" data-testid="summary-outlay">₹{summary.monthlyOutlay.toLocaleString("en-IN")}</div></div>
              </>
            ) : (
              <>
                <div><div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Total promoters</div><div className="text-2xl font-bold" data-testid="summary-total">{summary.total}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Booked</div><div className="text-2xl font-bold text-green-700" data-testid="summary-booked">{summary.booked}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Pending</div><div className="text-2xl font-bold text-amber-700" data-testid="summary-pending">{summary.pending}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Amount ({month})</div><div className="text-2xl font-bold" data-testid="summary-amount">₹{summary.amountBooked.toLocaleString("en-IN")}</div></div>
              </>
            )}
          </Card>

          {loading ? (
            <Card className="p-8 text-center text-gray-500">Loading…</Card>
          ) : promoters.length === 0 ? (
            <Card className="p-8 text-center text-gray-500" data-testid="salary-empty">No promoters yet.</Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="p-3 text-left">Promoter</th>
                      <th className="p-3 text-left">Phone</th>
                      <th className="p-3 text-left">Monthly Salary</th>
                      {tab === "masters" && <>
                        <th className="p-3 text-left">Aadhaar</th>
                        <th className="p-3 text-left">PAN</th>
                        <th className="p-3 text-left">Bank / IFSC</th>
                      </>}
                      {tab === "bookings" && <>
                        <th className="p-3 text-left">Status ({month})</th>
                        <th className="p-3 text-left">Amount</th>
                      </>}
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoters.map(u => {
                      const m = masterByUser[u.id];
                      const b = bookingByUser[u.id];
                      return (
                        <tr key={u.id} className="border-t border-gray-100" data-testid={`salary-row-${u.id}`}>
                          <td className="p-3 font-medium">{u.name}</td>
                          <td className="p-3 font-mono text-gray-600">{u.phone}</td>
                          <td className="p-3">₹{(m?.monthly_salary || u.monthly_salary || 0).toLocaleString("en-IN")}</td>
                          {tab === "masters" && <>
                            <td className="p-3 font-mono text-xs" data-testid={`aadhaar-cell-${u.id}`}>
                              {m?.aadhaar_masked || <span className="text-gray-400">not set</span>}
                            </td>
                            <td className="p-3 font-mono text-xs" data-testid={`pan-cell-${u.id}`}>
                              {m?.pan_masked || <span className="text-gray-400">not set</span>}
                            </td>
                            <td className="p-3 text-xs text-gray-600">
                              {m?.bank_account ? `${maskBank(m.bank_account)} · ${m.ifsc || "—"}` : <span className="text-gray-400">not set</span>}
                            </td>
                          </>}
                          {tab === "bookings" && <>
                            <td className="p-3">
                              {b ? <Badge className="bg-green-600 text-white" data-testid={`booking-badge-${u.id}`}>Booked</Badge>
                                 : <Badge variant="outline" data-testid={`booking-badge-${u.id}`}>Pending</Badge>}
                            </td>
                            <td className="p-3 font-medium">{b ? `₹${b.net_amount.toLocaleString("en-IN")}` : "—"}</td>
                          </>}
                          <td className="p-3 text-right space-x-2 whitespace-nowrap">
                            {tab === "masters" && (
                              <Button size="sm" variant="outline" data-testid={`edit-master-btn-${u.id}`} onClick={() => openMasterEdit(u)}>
                                <IdCard className="h-3.5 w-3.5 mr-1" /> {m ? "Update" : "Set up"}
                              </Button>
                            )}
                            {tab === "bookings" && !b && (
                              <Button size="sm" data-testid={`book-btn-${u.id}`} className="bg-green-600 hover:bg-green-700" onClick={() => openBook(u)}>
                                <PlusCircle className="h-3.5 w-3.5 mr-1" /> Book
                              </Button>
                            )}
                            {tab === "bookings" && b && (
                              <span className="text-xs text-gray-500">Booked by {b.booked_by_name} · linked expense</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </main>
      </div>

      {/* Salary master edit dialog */}
      <Dialog open={!!editForm} onOpenChange={(o) => !o && setEditForm(null)}>
        <DialogContent data-testid="master-edit-dialog">
          <DialogHeader><DialogTitle>{editForm?.user_name} — Salary Master</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-700">Monthly Salary (₹)*</label>
                <Input data-testid="master-salary-input" type="number" className="h-11 mt-1"
                  value={editForm.monthly_salary}
                  onChange={(e) => setEditForm(f => ({ ...f, monthly_salary: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Aadhaar (12 digits) {editForm.aadhaar_masked && <span className="text-gray-500">— current: {editForm.aadhaar_masked}</span>}</label>
                <Input data-testid="master-aadhaar-input" className="h-11 mt-1" placeholder="Enter to update, leave blank to keep"
                  value={editForm.aadhaar}
                  onChange={(e) => setEditForm(f => ({ ...f, aadhaar: e.target.value.replace(/\D/g, "").slice(0, 12) }))} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">PAN (AAAAA9999A) {editForm.pan_masked && <span className="text-gray-500">— current: {editForm.pan_masked}</span>}</label>
                <Input data-testid="master-pan-input" className="h-11 mt-1" placeholder="Enter to update, leave blank to keep"
                  value={editForm.pan}
                  onChange={(e) => setEditForm(f => ({ ...f, pan: e.target.value.toUpperCase().slice(0, 10) }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700">Bank Account</label>
                  <Input data-testid="master-bank-input" className="h-11 mt-1"
                    value={editForm.bank_account} onChange={(e) => setEditForm(f => ({ ...f, bank_account: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">IFSC</label>
                  <Input data-testid="master-ifsc-input" className="h-11 mt-1"
                    value={editForm.ifsc} onChange={(e) => setEditForm(f => ({ ...f, ifsc: e.target.value.toUpperCase() }))} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Notes</label>
                <Input data-testid="master-notes-input" className="h-11 mt-1"
                  value={editForm.notes} onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <p className="text-[11px] text-gray-500">Aadhaar / PAN are stored securely and always shown masked. No user or export can retrieve the raw value.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditForm(null)}>Cancel</Button>
            <Button className="bg-brand-red hover:bg-[#B31721]" onClick={saveMaster} disabled={saving} data-testid="master-save-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Book salary dialog */}
      <Dialog open={!!bookForm} onOpenChange={(o) => !o && setBookForm(null)}>
        <DialogContent data-testid="book-salary-dialog">
          <DialogHeader><DialogTitle>Book Salary — {bookForm?.user_name}</DialogTitle></DialogHeader>
          {bookForm && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-700">Month</label>
                <Input value={bookForm.month} readOnly className="h-11 mt-1 bg-gray-50" data-testid="book-month-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Net Amount (₹)*</label>
                <Input data-testid="book-amount-input" type="number" className="h-11 mt-1"
                  value={bookForm.net_amount}
                  onChange={(e) => setBookForm(f => ({ ...f, net_amount: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700">Remarks</label>
                <Input data-testid="book-remarks-input" className="h-11 mt-1"
                  value={bookForm.remarks} onChange={(e) => setBookForm(f => ({ ...f, remarks: e.target.value }))} />
              </div>
              <p className="text-[11px] text-gray-500">Booking creates an approved central expense (category: Salary, non-billable to Coca-Cola).</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookForm(null)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={doBook} disabled={saving} data-testid="book-confirm-btn">
              {saving ? "Booking…" : "Book & post expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
